import React, { useEffect, useState } from 'react';
import PropTypes, { string } from 'prop-types';
import { connect } from 'react-redux';
import { IntlProvider, FormattedMessage } from 'react-intl';
import { PropertiesLayout, FeedbackMessage, Title } from '@kognitiv/bf-library';
import {
    scrollToTop, getPropertyCardProps, getTrustYouProfiles,
    getNumberOfNights, getDisplayModeForProperties, getPaginatorReferenceLabel,
} from '../../../shared/utils/UtilsHelper';
import { getQueryString } from '../../../services/QueryParameterService';
import WidgetContext from 'State/WidgetContext';
import * as Search from 'Actions/search';
import * as propertiesActions from 'Actions/hotels';
import { getUserCountry } from 'Actions/settings';
import { updateUrl } from 'Actions/url';
import { getTrustYouProfile } from 'Actions/trustYou';
import {
    propertyShape, paginationShape, propertiesFiltersShape, trustYouReducerShape,
    translationsReducerShape, propertyListContainerConfigShape,
} from 'Constants/PropTypes';
import propertiesSortByOptions from 'Constants/sortByConfig';
import filterDataAndLabels from 'Constants/PropertiesFilters';
import { ECHO_WIDGET } from 'Constants/WidgetConfig';
import { QUERY_PARAMETERS } from 'Constants/QueryParameters';
import { appendScriptToHead } from 'Helpers';
import { useHotel } from 'State/HotelsContext';
import CouponMandatoryForm from 'Components/List/CouponMandatoryForm';
import COUPON_INTEGRATION from 'Constants/CouponIntegration';
import CouponInfo from 'Components/SearchBar/CouponInfo';

function PropertyListContainer(props) {
    const {
        id, offers, nonAvailableOffers, translations, associatedCountries,
        isLoading, error, language, onViewRooms, domain, getQueryParams,
        dispatchGetTrustYouProfile, dispatchGetUserCountry, trustYou,
        config, widgetType, priceDisplayMode, onChangePage, search,
        onChangePageSize, pagination, priceRangeMin, priceRangeMax,
        onFilterPrice, onFilterCheckboxes, autocompleteFilterApplied, filters, dispatchGetAssociatedCountries,
        onPropertiesRendered, onChangeSortBy, sortBy, currency, dispatchGetHotelsList,
        userCountry, searchBarConfiguration, couponIntegration, dispatchApplyCoupon, channelId, coupon, couponVoucherTerm,
        dispatchUpdateCoupon, hidePrices, totalResults,
        dispatchGetPortalFilters, portalFilters, dispatchLoadClassifications,
        alternativeOffersConfig, showAlternativeOffers, dispatchToggleAlternativeOffers,
        resetAutocompleteFilters, setAutocompleteFilter, onFilterProperties, onChangeSearchInput,
    } = props;

    const [ queryParams, setQueryParams ] = useState({});
    const [ prevOffers, setPrevOffers ] = useState(null);
    const [ prevNonAvailableOffers, setPrevNonAvailableOffers ] = useState(null);
    const [ firstLoad, setFirstLoad ] = useState(true);

    // placeholder label cant be <FormattedMessage>
    const intlProvider = new IntlProvider({ locale: language, messages: translations.data });
    const { intl } = intlProvider.getChildContext();
    const searchInputPlaceHolder = intl.formatMessage({ id: 'bf.locationOrHotelName', defaultMessage: 'bf.locationOrHotelName' });
    const newFilterDataAndLabels = { ...filterDataAndLabels, searchInputPlaceHolder };

    useEffect(() => {
        if (widgetType === 'KUBE') {
            dispatchGetAssociatedCountries();
        }
    }, []);

    useEffect(() => {
        if (!isLoading) {
            const newParams = getQueryParams(id, null, null, widgetType === ECHO_WIDGET).query;
            setQueryParams(newParams);
        }
    }, [ isLoading, search ]);

    // TrustYou functions
    useEffect(() => {
        if (widgetType === 'KUBE') {
            getTrustYouProfiles(
                prevOffers,
                setPrevOffers,
                offers,
                prevNonAvailableOffers,
                setPrevNonAvailableOffers,
                nonAvailableOffers,
                language,
                dispatchGetTrustYouProfile,
            );
        }
    }, [ offers, nonAvailableOffers ]);

    useEffect(() => {
        dispatchGetUserCountry();
        if (firstLoad) {
            setFirstLoad(false);
            dispatchLoadClassifications(language);
            dispatchGetPortalFilters(language);
        }

        dispatchGetHotelsList();

    }, [ currency, language ]);

    useEffect(() => {
        if (couponIntegration === COUPON_INTEGRATION.SEARCH_MANDATORY) {
            dispatchGetHotelsList();
        }
    }, [ coupon ]);

    // Just for eec, for running onPropertiesRendered
    useEffect(() => {
        if (onPropertiesRendered) {
            onPropertiesRendered('hotelsList', totalResults);
        }
    }, [ totalResults ]);

    // config props to enable functionallity on propertiesLayout
    const layout = config?.layout;
    const availableLayouts = [ 'list', 'grid', 'map' ];
    const styledPrice = config?.styledPrice;
    const alternativeGrid = config?.view === 'simplified';
    // Defaults to true for all values including undefined, with exception of false
    const showViewSelector = config?.showViewSelector !== false;
    const showFilters = config?.showFilters !== false;
    const showSortingSelector = config?.showSortingSelector !== false;

    let hideNameFilter = !config?.autocompleteFilterEnabled;

    if (searchBarConfiguration?.destinationsFilterEnabled) {
        hideNameFilter = true;
    }

    const hideFilterSections = {
        hideNameFilter         : config?.hideNameFilter || hideNameFilter,
        hideCountriesFilter    : config?.hideCountriesFilter,
        hideMealPlanFilter     : config?.hideMealPlanFilter,
        hidePropertyTypeFilter : config?.hidePropertyTypeFilter,
        hideStarsFilter        : config?.hideStarsFilter,
    };

    const propertyCardProps = getPropertyCardProps(props);

    // Pagination props
    const intlItems = (<FormattedMessage id="bf.hotels" defaultMessage="bf.hotels" />);
    const paginatorReferenceLabel = pagination
                    && getPaginatorReferenceLabel(
                        pagination.currentPage,
                        pagination.pageSize,
                        pagination.totalResults,
                        intlItems,
                    );
    const paginatorPageSizeMessage = <FormattedMessage id="bf.pageSize" defaultMessage="bf.pageSize" values={{ items: intlItems }} />;

    // Sort by props
    const handleChangeSortBy = ({ value }) => {
        const newValue = value === 'recommended' ? '' : value;
        return sortBy !== newValue && onChangeSortBy(value);
    };
    const sortByMenuItems = showSortingSelector && (
        propertiesSortByOptions.map((option, index) => (
            {
                ...option,
                onClick       : () => handleChangeSortBy(option),
                isHighlighted : (index === 0 && sortBy === '') || option.value === sortBy,
            }
        ))
    );

    // Properties messages
    const feedbackMessages = {
        noAvailableOffersMessage: (
            <FeedbackMessage
                type="error"
                message={<FormattedMessage id="bf.noOffersAvailable" defaultMessage="bf.noOffersAvailable" />}
            />),
        notAvailablePropertiesTitleMessage: (
            <Title
                content={<FormattedMessage
                    id="bf.hotelsUnavailableForCurrentSearch"
                    defaultMessage="bf.hotelsUnavailableForCurrentSearch"
                />}
                className="margin-top-medium margin-bottom-medium"
            />
        ),
        buttonMessage: widgetType === 'KUBE'
            ? (<FormattedMessage id="bf.bookNow" defaultMessage="bf.bookNow" />)
            : (<FormattedMessage id="bf.viewRooms" defaultMessage="bf.viewRooms" />),
        reviewsCountMessage: reviewsCount => (
            <FormattedMessage
                id="bf.reviewsCount"
                defaultMessage="bf.reviewsCount"
                values={{ quantity: reviewsCount }}
            />
        ),
        reviewsModalTitleMessage : <FormattedMessage id="bf.reviews" defaultMessage="bf.reviews" />,
        sortByLabel              : <FormattedMessage id="bf.sortBy" defaultMessage="bf.sortBy" />,

    };

    const viewAsLabel = (<FormattedMessage id="bf.viewAs" defaultMessage="bf.viewAs" />);

    // Price messages
    const nights = queryParams && getNumberOfNights(queryParams[QUERY_PARAMETERS.CHECKIN],
        queryParams[QUERY_PARAMETERS.CHECKOUT]);
    const priceMessages = {
        startingFrom     : (<FormattedMessage id="bf.startingFrom" defaultMessage="bf.startingFrom" />),
        priceDisplayMode : getDisplayModeForProperties(priceDisplayMode, nights),
    };
    // Map messages
    const labelsMapLayout = {
        propertiesCurrentSearch : (<FormattedMessage id="bf.propertiesForCurrentSearch" defaultMessage="bf.propertiesForCurrentSearch" />),
        availableIcon           : (<FormattedMessage id="bf.available" defaultMessage="bf.available" />),
        notAvailableIcon        : (<FormattedMessage id="bf.notAvailable" defaultMessage="bf.notAvailable" />),
        availableAnd            : (<FormattedMessage id="bf.availableAnd" defaultMessage="bf.availableAnd" />),
        filterButton            : (<FormattedMessage id="bf.filters" defaultMessage="bf.filters" />),
        sortAndFilterButton     : (<FormattedMessage id="bf.sortAndFilter" defaultMessage="bf.sortAndFilter" />),
    };

    const alternativeOffersLabels = {
        show              : (<FormattedMessage id="bf.showAlternativePrices" defaultMessage="bf.showAlternativePrices" />),
        hide              : (<FormattedMessage id="bf.hideAlternativePrices" defaultMessage="bf.hideAlternativePrices" />),
        getTitleWithHotel : hotelName => (<FormattedMessage
            id="bf.alternativeOffersForProperty"
            defaultMessage="bf.alternativeOffersForProperty"
            values={{ property: hotelName }}
        />),
        title             : (<FormattedMessage id="bf.alternativePrices" defaultMessage="bf.alternativePrices" />),
        getCheckIn        : los => (
            <>
                <FormattedMessage id="bf.checkIn" defaultMessage="bf.checkIn" />
                {' ('}
                <FormattedMessage
                    id="bf.stayLos"
                    defaultMessage="bf.stayLos"
                    values={{ los: (<FormattedMessage id="bf.nightsCount" defaultMessage="bf.nightsCount" values={{ count: los }} />) }}
                />
                {')'}
            </>
        ),
        getStartingFrom: pdm => (
            <>
                <FormattedMessage id="bf.startingFrom" defaultMessage="bf.startingFrom" />
                {' ('}
                { pdm === 'stay'
                    ? <FormattedMessage id="bf.total" defaultMessage="bf.total" />
                    : pdm }
                {')'}
            </>
        ),
    };

    const hotelContext = useHotel();

    const filterProps = {
        resetAutocompleteFilters : hotelContext.resetAutocompleteFilters || resetAutocompleteFilters,
        setAutocompleteFilter    : hotelContext.setAutocompleteFilter || setAutocompleteFilter,
        onFilterProperties       : hotelContext.onFilterProperties || onFilterProperties,
        onChangeSearchInput      : hotelContext.onChangeSearchInput || onChangeSearchInput,
        priceRangeMin,
        priceRangeMax,
        onFilterPrice,
        onFilterCheckboxes,
        autocompleteFilterApplied,
        showFilters,
        associatedCountries,
        hideFilterSections,
        filters,
        filterDataAndLabels      : newFilterDataAndLabels,
    };
    const displayCouponForm = couponIntegration === COUPON_INTEGRATION.SEARCH_MANDATORY && !coupon;

    return (
        <WidgetContext.Provider value={{ id }}>
            {
                <IntlProvider locale={language} messages={translations.data}>
                    {displayCouponForm ? (
                        <CouponMandatoryForm
                            applyCoupon={dispatchApplyCoupon}
                            channelId={channelId}
                            couponVoucherTerm={couponVoucherTerm}
                        />
                    )
                        : (
                            <>
                                {coupon ? (
                                    <CouponInfo
                                        couponCode={coupon}
                                        couponVoucherTerm={couponVoucherTerm}
                                        applyCoupon={dispatchApplyCoupon}
                                        updateCoupon={dispatchUpdateCoupon}

                                    />
                                )
                                    : null}
                                <PropertiesLayout
                                    displayMode={layout}
                                    labelsMapLayout={labelsMapLayout}
                                    propertyCardProps={propertyCardProps}
                                    availableProperties={offers}
                                    feedbackMessages={feedbackMessages}
                                    notAvailableProperties={nonAvailableOffers}
                                    language={language}
                                    isLoading={isLoading}
                                    error={error}
                                    onClick={onViewRooms}
                                    params={queryParams}
                                    domain={domain}
                                    getQueryString={getQueryString}
                                    trustYou={trustYou}
                                    selectorViewAvailable={showViewSelector}
                                    availableLayouts={availableLayouts}
                                    alternativeGrid={alternativeGrid}
                                    priceMessages={priceMessages}
                                    styledPrice={styledPrice}
                                    onChangePage={onChangePage}
                                    onChangePageSize={onChangePageSize}
                                    sortBy={sortBy}
                                    sortByMenuItems={sortByMenuItems}
                                    id={id}
                                    pagination={pagination}
                                    paginatorReferenceLabel={paginatorReferenceLabel}
                                    paginatorPageSizeMessage={paginatorPageSizeMessage}
                                    viewAsLabel={viewAsLabel}
                                    userCountry={userCountry}
                                    onGetPlace={hotelContext.handleOnGetPlace}
                                    isQa
                                    hidePrices={hidePrices}
                                    hidePricesMessage={<FormattedMessage
                                        id="bf.bookeableWithCouponTerminology"
                                        defaultMessage="bf.bookeableWithCouponTerminology"
                                        values={{ couponVoucherTerm }}
                                    />}
                                    {...filterProps}
                                    portalFilters={portalFilters}
                                    alternativeOffersConfig={alternativeOffersConfig}
                                    showAlternativeOffers={showAlternativeOffers}
                                    toggleAlternativeOffers={dispatchToggleAlternativeOffers}
                                    alternativeOffersLabels={alternativeOffersLabels}
                                    intl={intl}
                                />
                            </>
                        )}
                </IntlProvider>
            }

        </WidgetContext.Provider>

    );
}

PropertyListContainer.defaultProps = {
    offers                    : [],
    nonAvailableOffers        : [],
    onViewRooms               : null,
    trustYou                  : {},
    priceRangeMin             : null,
    priceRangeMax             : null,
    autocompleteFilterApplied : null,
    priceDisplayMode          : '',
    associatedCountries       : null,
    sortBy                    : '',
    onPropertiesRendered      : null,
    hidePrices                : false,
    couponVoucherTerm         : '',
    totalResults              : 0,
};

PropertyListContainer.propTypes = {
    language                       : PropTypes.string.isRequired,
    id                             : PropTypes.string.isRequired,
    offers                         : PropTypes.arrayOf(PropTypes.shape(propertyShape)),
    nonAvailableOffers             : PropTypes.arrayOf(PropTypes.shape(propertyShape)),
    isLoading                      : PropTypes.bool.isRequired,
    error                          : PropTypes.bool.isRequired,
    onViewRooms                    : PropTypes.func,
    domain                         : PropTypes.string.isRequired,
    getQueryParams                 : PropTypes.func.isRequired,
    translations                   : PropTypes.shape(translationsReducerShape).isRequired,
    dispatchGetUserCountry         : PropTypes.func.isRequired,
    dispatchGetTrustYouProfile     : PropTypes.func.isRequired,
    trustYou                       : PropTypes.objectOf(PropTypes.shape(trustYouReducerShape)),
    config                         : PropTypes.shape(propertyListContainerConfigShape).isRequired,
    widgetType                     : PropTypes.string.isRequired,
    priceDisplayMode               : PropTypes.string,
    onChangePage                   : PropTypes.func.isRequired,
    onChangePageSize               : PropTypes.func.isRequired,
    pagination                     : PropTypes.shape(paginationShape).isRequired,
    priceRangeMin                  : PropTypes.number,
    priceRangeMax                  : PropTypes.number,
    onFilterProperties             : PropTypes.func,
    onChangeSearchInput            : PropTypes.func,
    onFilterPrice                  : PropTypes.func.isRequired,
    onFilterCheckboxes             : PropTypes.func.isRequired,
    resetAutocompleteFilters       : PropTypes.func,
    setAutocompleteFilter          : PropTypes.func,
    dispatchGetAssociatedCountries : PropTypes.func.isRequired,
    associatedCountries            : PropTypes.objectOf(string),
    autocompleteFilterApplied      : PropTypes.string,
    filters                        : PropTypes.shape(propertiesFiltersShape).isRequired,
    onChangeSortBy                 : PropTypes.func.isRequired,
    sortBy                         : PropTypes.string,
    onPropertiesRendered           : PropTypes.func,
    hidePrices                     : PropTypes.bool,
    couponVoucherTerm              : PropTypes.string,
    totalResults                   : PropTypes.number,
};

const mapStateToProps = (state, ownProps) => {
    const offers = state.hotels.data?.properties;
    const nonAvailableOffers = state.hotels.data?.nonAvailableProperties;
    const widgetSet = state.widgetPool.find(widgetSet => widgetSet.config.id === ownProps.id);
    const configuration = widgetSet?.hotels;
    const searchBarConfiguration = widgetSet?.searchbar;
    const newTabDisabled = state.settings.data?.newTabDisabled;

    const showFeedbackMessage = configuration?.showFeedbackMessage;

    return {
        isLoading : state.hotels.isLoading,
        config    : configuration,
        error     : state.hotels.error,
        currency  : state.search.data.currency,
        domain    : state.settings.data.shouldTakeRedirectDomainFromApi
            ? state.settings.data.ntpDomain : state.settings.data.domain,
        id                        : state.widgetPool[0].config.id,
        currencyDisplay           : state.settings.data.currencyDisplay,
        priceDisplayMode          : state.hotels.data && state.hotels.data.priceDisplayMode,
        totalResults              : state.hotels.data?.totalResults,
        pagination                : state.hotels.pagination,
        sortBy                    : state.hotels.sortBy,
        language                  : state.search.data.language,
        search                    : state.search.data,
        userCountry               : state.settings.data && state.settings.data.userCountry,
        translations              : state.translations,
        trustYou                  : state.trustYou,
        widgetType                : state.settings.data.widgetType,
        priceRangeMin             : state.hotels.priceRangeMin,
        priceRangeMax             : state.hotels.priceRangeMax,
        autocompleteFilterApplied : state.hotels.autocompleteFilter,
        filters                   : state.hotels.filters,
        associatedCountries       : state.hotels.associatedCountries,
        offers,
        nonAvailableOffers,
        showFeedbackMessage,
        newTabDisabled,
        searchBarConfiguration,
        couponIntegration         : state.settings.data.couponIntegration,
        channelId                 : state.settings.data.channelId,
        coupon                    : state.hotels.coupon || state.search.data?.coupon,
        couponVoucherTerm         : state.settings.data.couponVoucherTerm,
        hidePrices                : state.hotels.data?.hidePrices,
        portalFilters             : state.hotels.portalFilters || {},
        showAlternativeOffers     : state.hotels.showAlternativeOffers,
        alternativeOffersConfig   : state.settings.data?.alternativeOffers,
    };
};

const mapDispatchToProps = (dispatch) => {
    const getQueryParams = (id, code, isOffer, readable) => dispatch(Search.getURLParams(id, code, isOffer, readable));
    const dispatchGetUserCountry = async () => dispatch(getUserCountry()).then((response) => {
        if (response === 'CN') {
            appendScriptToHead('https://api.map.baidu.com/api?v=2.0&ak=aErqGng1dsPhG12AAtmIP4v5tIy9b7Lg', 'baiduScript');
            // eslint-disable-next-line max-len
            appendScriptToHead('https://api.map.baidu.com/getscript?v=2.0&ak=aErqGng1dsPhG12AAtmIP4v5tIy9b7Lg&services=&t=20191112103629', 'baiduScript2');
        }
    });
    const dispatchGetHotelsList = async () => dispatch(propertiesActions.getHotelsList());
    const dispatchGetTrustYouProfile = async (trustYouId, language) => dispatch(getTrustYouProfile(trustYouId, language));
    const dispatchGetAssociatedCountries = async () => dispatch(propertiesActions.getAssociatedCountries());
    const dispatchGetPortalFilters = async language => dispatch(propertiesActions.getPortalFilters(language));
    const dispatchLoadClassifications = async language => dispatch(propertiesActions.loadClassifications(language));
    const onChangePage = async (page, id) => {
        scrollToTop(10, 40);
        const { pages, newUrlState } = await dispatch(propertiesActions.setPage(page));
        if (!pages[page]) {
            dispatch(propertiesActions.getHotelsList(null, id));
        }
        dispatch(updateUrl(newUrlState));
    };

    const onChangePageSize = async (pageSize) => {
        scrollToTop(10, 40);
        await dispatch(propertiesActions.resetPagination());
        const { newUrlState } = await dispatch(propertiesActions.setPageSize(pageSize));
        dispatch(updateUrl(newUrlState));
        dispatch(propertiesActions.getHotelsList());
    };

    const onChangeSortBy = (sortOrder) => {
        dispatch(propertiesActions.cleanPages());
        dispatch(propertiesActions.sortProperties(sortOrder));
        dispatch(propertiesActions.resetPagination());
        dispatch(propertiesActions.getHotelsList());
    };

    const trackAndGetProperties = async () => {
        scrollToTop(10, 40);
        dispatch(propertiesActions.resetPagination());
        await dispatch(propertiesActions.getHotelsList());
    };

    const onFilterPrice = async (minPrice, maxPrice) => {
        await dispatch(propertiesActions.changePriceFilter(minPrice, maxPrice));
        trackAndGetProperties();
    };
    const onFilterCheckboxes = async (filterName, value) => {
        await dispatch(propertiesActions.changeCheckboxesFilter(filterName, value));
        trackAndGetProperties();
    };

    const dispatchToggleAlternativeOffers = async () => {
        await dispatch(propertiesActions.toggleAlternativeOffersShown());
        trackAndGetProperties();
    };

    const dispatchApplyCoupon = (code) => {
        dispatch(propertiesActions.applyCoupon(code));
    };

    const dispatchUpdateCoupon = (code) => {
        dispatch(Search.updateCoupon(code));
    };

    return {
        getQueryParams,
        dispatchGetUserCountry,
        dispatchGetHotelsList,
        dispatchGetTrustYouProfile,
        onFilterPrice,
        onFilterCheckboxes,
        onChangePage,
        onChangePageSize,
        onChangeSortBy,
        dispatchGetAssociatedCountries,
        dispatchApplyCoupon,
        dispatchUpdateCoupon,
        dispatchGetPortalFilters,
        dispatchLoadClassifications,
        dispatchToggleAlternativeOffers,
    };
};
export default connect(mapStateToProps, mapDispatchToProps)(PropertyListContainer);

// i18nLabel[bf.openHotel]
